/**
 * Formatting for the static SEO pages.
 *
 * Everything here must produce identical strings in Node (build-time render)
 * and in the browser (hydration), so dates are formatted from their ISO
 * strings by hand rather than through Date/locale APIs, which shift with the
 * machine's timezone.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MINUS = '−';

export const EMPTY = '—';

export function isNum(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function trimZeros(text) {
  return text.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
}

/** $1.85M, $950k, $12,500. */
export function formatMoneyCompact(value) {
  if (!isNum(value)) return EMPTY;
  const abs = Math.abs(value);
  if (abs >= 1e6) {
    const digits = abs >= 1e7 ? 1 : 2;
    return `$${trimZeros((value / 1e6).toFixed(digits))}M`;
  }
  if (abs >= 1e4) return `$${Math.round(value / 1e3)}k`;
  return `$${formatInt(value)}`;
}

export function formatInt(value) {
  if (!isNum(value)) return EMPTY;
  return String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** Unsigned percentage, one decimal: 8.2% */
export function formatPct(value, digits = 1) {
  if (!isNum(value)) return EMPTY;
  return `${Math.abs(value).toFixed(digits)}%`;
}

/** Signed gap with a true minus sign: +8.2%, −3.1%, 0.0% */
export function formatGap(value, digits = 1) {
  if (!isNum(value)) return EMPTY;
  const rounded = Number(value.toFixed(digits));
  if (rounded === 0) return `${(0).toFixed(digits)}%`;
  return `${rounded > 0 ? '+' : MINUS}${Math.abs(rounded).toFixed(digits)}%`;
}

/** Signed percentage-point change: +2.3 pts */
export function formatPoints(value) {
  if (!isNum(value)) return EMPTY;
  const rounded = Number(value.toFixed(1));
  const sign = rounded > 0 ? '+' : rounded < 0 ? MINUS : '';
  return `${sign}${Math.abs(rounded).toFixed(1)} pts`;
}

/**
 * Direction of a signed guide gap. Anything within ±0.5% reads as "at guide"
 * so rounding noise never flips the story.
 */
export function gapDirection(value) {
  if (!isNum(value)) return 'none';
  if (value >= 0.5) return 'above';
  if (value <= -0.5) return 'below';
  return 'at';
}

export function gapWords(value) {
  const direction = gapDirection(value);
  if (direction === 'above') return 'above guide';
  if (direction === 'below') return 'below guide';
  if (direction === 'at') return 'at guide';
  return 'no guide data';
}

/** "$1.5M–$1.65M", or "$1.5M" for single-figure guides. */
export function formatGuideRange(low, high) {
  if (!isNum(low) && !isNum(high)) return EMPTY;
  if (!isNum(high) || low === high) return formatMoneyCompact(low);
  if (!isNum(low)) return formatMoneyCompact(high);
  return `${formatMoneyCompact(low)}–${formatMoneyCompact(high)}`;
}

/** "2025-10" -> "Oct 2025" */
export function formatMonth(value) {
  const match = /^(\d{4})-(\d{2})/.exec(value || '');
  if (!match) return EMPTY;
  return `${MONTHS[Number(match[2]) - 1]} ${match[1]}`;
}

/** "2025-10" -> "Oct" (axis ticks) */
export function formatMonthShort(value) {
  const match = /^(\d{4})-(\d{2})/.exec(value || '');
  if (!match) return '';
  return MONTHS[Number(match[2]) - 1];
}

/** "2026-09-13" -> "13 Sep 2026" */
export function formatDay(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
  if (!match) return EMPTY;
  return `${Number(match[3])} ${MONTHS[Number(match[2]) - 1]} ${match[1]}`;
}

/** Whole months between two ISO dates, rounded. */
export function monthsBetween(from, to) {
  const a = /^(\d{4})-(\d{2})-(\d{2})/.exec(from || '');
  const b = /^(\d{4})-(\d{2})-(\d{2})/.exec(to || '');
  if (!a || !b) return null;
  const months = (Number(b[1]) - Number(a[1])) * 12 + (Number(b[2]) - Number(a[2]));
  const dayShift = (Number(b[3]) - Number(a[3])) / 30;
  return Math.round(months + dayShift);
}

/** "the last 12 months" or "Mar 2025 to Sep 2026". */
export function describePeriod(period) {
  if (!period) return 'the period measured';
  const months = monthsBetween(period.from, period.to);
  if (months === 12) return 'the last 12 months';
  if (months === 24) return 'the last two years';
  return `${formatMonth(period.from)} to ${formatMonth(period.to)}`;
}

const METHOD_LABELS = {
  auction: 'Auction',
  private_treaty: 'Private treaty',
  unknown: EMPTY,
};

export function formatSaleMethod(value) {
  return METHOD_LABELS[value] || EMPTY;
}

const TYPE_LABELS = {
  house: 'House',
  unit: 'Unit',
  townhouse: 'Townhouse',
  land: 'Land',
  other: 'Other',
};

export function formatPropertyType(value, bedrooms) {
  const label = TYPE_LABELS[value] || 'Property';
  return isNum(bedrooms) && bedrooms > 0 ? `${bedrooms} bed ${label.toLowerCase()}` : label;
}

/** Stable 32-bit FNV-1a hash, used to pick copy variants deterministically. */
export function stableHash(text) {
  let hash = 0x811c9dc5;
  const input = String(text || '');
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

export function pickVariant(seed, variants) {
  return variants[stableHash(seed) % variants.length];
}

export function ordinal(n) {
  if (!isNum(n)) return EMPTY;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th';
  return `${n}${suffix}`;
}
